import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from './constants';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import dayjs from 'dayjs';
import { 
  Globe, 
  TrendingUp, 
  ChatCircle, 
  CurrencyDollar, 
  MapPin,
  Image as ImageIcon,
  Link as LinkIcon,
  Storefront
} from '@phosphor-icons/react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";

const MAP_THEMES = {
  default: {
    title: 'World Map: Global Data Distribution',
    subtitle: 'Interactive world map showing data distribution across global markets - countries are color-coded by number of records/banners',
    mapTitle: '🌍 Global Data Distribution Map',
    mapHint: '💡 Click on any country to view marketing communication and banners for that specific market',
    dataDistributionTitle: 'Data Distribution',
    legendTitle: 'Color Scale (Total Data Counts):',
    mapContainerBg: '#f8f9fa',
    mapContainerBorder: '2px solid #e0e0e0',
    zoomControlBg: '#ffffff',
    zoomControlBorder: '#ccc',
    zoomControlHoverBg: '#f5f5f5',
    mapStroke: '#cccccc',
    noDataFill: '#ffffff',
    noDataHoverFill: '#f0f0f0',
    tooltipBg: '#ffffff',
    tooltipBorder: '#ccc',
    titleColor: 'inherit',
    sectionTitleColor: 'inherit',
    gradientStart: null,
    gradientEnd: null,
    legendItems: [
      { color: '#b40000', label: 'Few records (dark red)' },
      { color: '#b4b400', label: 'Medium records (dark yellow)' },
      { color: '#00b400', label: 'Many records (dark green)' },
      { color: '#ffffff', label: 'No data (white)', border: '#ccc' }
    ]
  },
  green: {
    title: 'World Map: Global Data Distribution',
    subtitle: 'Interactive world map showing data distribution across global markets - countries are color-coded by number of records/banners',
    mapTitle: '🌍 Global Data Distribution Map',
    mapHint: '💡 Click on any country to view marketing communication and banners for that specific market',
    dataDistributionTitle: 'Data Distribution',
    legendTitle: 'Color Scale (Total Data Counts):',
    mapContainerBg: '#e5e5e5',
    mapContainerBorder: '2px solid #c7d3c0',
    zoomControlBg: '#ffffff',
    zoomControlBorder: '#a5d6a7',
    zoomControlHoverBg: '#e8f5e9',
    mapStroke: '#c0c0c0',
    noDataFill: '#f7f7f7',
    noDataHoverFill: '#e0e0e0',
    tooltipBg: '#f4fff1',
    tooltipBorder: '#a5d6a7',
    titleColor: '#2e7d32',
    sectionTitleColor: '#1b5e20',
    gradientStart: '#d7f5d0',
    gradientEnd: '#1b5e20',
    legendItems: [
      { color: '#d7f5d0', label: 'Low incidence' },
      { color: '#81c784', label: 'Medium incidence' },
      { color: '#2e7d32', label: 'High incidence' },
      { color: '#f7f7f7', label: 'No data', border: '#c7c7c7' }
    ]
  }
};

const getMapTheme = (variant) => MAP_THEMES[variant] || MAP_THEMES.default;

const hexToRgb = (hex) => {
  if (!hex) return { r: 0, g: 0, b: 0 };
  const normalized = hex.replace('#', '');
  const value = normalized.length === 3
    ? normalized.split('').map((ch) => ch + ch).join('')
    : normalized;
  const intValue = parseInt(value, 16);
  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255
  };
};

const interpolateColor = (startHex, endHex, ratio) => {
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);
  const clamp = Math.min(Math.max(ratio, 0), 1);
  const r = Math.round(start.r + (end.r - start.r) * clamp);
  const g = Math.round(start.g + (end.g - start.g) * clamp);
  const b = Math.round(start.b + (end.b - start.b) * clamp);
  return `rgb(${r}, ${g}, ${b})`;
};

const WorldMapView = ({ variant = 'default', showBanners = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ukData, setUkData] = useState(null);
  const [italyData, setItalyData] = useState(null);
  const [countryData, setCountryData] = useState({});
  const [mapData, setMapData] = useState([]);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ coordinates: [15, 45], zoom: 2 });
  const [colorScale, setColorScale] = useState({ min: 0, max: 1 });
  const [bannerData, setBannerData] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerError, setBannerError] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [bannerGroupBy, setBannerGroupBy] = useState('country'); // 'country' or 'retailer'
  const [expandedGroups, setExpandedGroups] = useState(new Set(['all'])); // Always expanded
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [sampleFilters, setSampleFilters] = useState({
    country: '',
    brand: '',
    retailer: '',
    category: '',
    flavor: '',
    strength: ''
  });
  const [sampleTab, setSampleTab] = useState('sticks');
  const theme = getMapTheme(variant);

  // Function to extract country from URL
  const extractCountryFromUrl = (url) => {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    
    // Common country patterns in URLs
    const countryPatterns = {
      'uk': ['uk', 'united kingdom', 'gb', 'great britain', 'england'],
      'italy': ['italy', 'it', 'italia'],
      'france': ['france', 'fr', 'francia'],
      'germany': ['germany', 'de', 'deutschland'],
      'spain': ['spain', 'es', 'espana'],
      'netherlands': ['netherlands', 'nl', 'holland', 'nederland'],
      'belgium': ['belgium', 'be', 'belgie'],
      'switzerland': ['switzerland', 'ch', 'schweiz'],
      'austria': ['austria', 'at', 'osterreich'],
      'poland': ['poland', 'pl', 'polska'],
      'czech': ['czech', 'cz', 'czech republic', 'ceska'],
      'hungary': ['hungary', 'hu', 'magyar'],
      'romania': ['romania', 'ro'],
      'bulgaria': ['bulgaria', 'bg'],
      'greece': ['greece', 'gr', 'ellada'],
      'sweden': ['sweden', 'se', 'sverige'],
      'norway': ['norway', 'no', 'norge'],
      'denmark': ['denmark', 'dk', 'danmark'],
      'finland': ['finland', 'fi', 'suomi'],
      'portugal': ['portugal', 'pt'],
      'ireland': ['ireland', 'ie', 'eire'],
      'luxembourg': ['luxembourg', 'lu', 'luxemburg'],
      'slovakia': ['slovakia', 'sk', 'slovensko'],
      'slovenia': ['slovenia', 'si', 'slovenija'],
      'croatia': ['croatia', 'hr', 'hrvatska'],
      'serbia': ['serbia', 'rs', 'srbija'],
      'bosnia': ['bosnia', 'ba', 'bosna'],
      'montenegro': ['montenegro', 'me', 'crna gora'],
      'albania': ['albania', 'al', 'shqiperi'],
      'north macedonia': ['macedonia', 'mk', 'makedonija'],
      'kosovo': ['kosovo', 'xk'],
      'moldova': ['moldova', 'md'],
      'ukraine': ['ukraine', 'ua', 'ukraina'],
      'belarus': ['belarus', 'by', 'belarus'],
      'lithuania': ['lithuania', 'lt', 'lietuva'],
      'latvia': ['latvia', 'lv', 'latvija'],
      'estonia': ['estonia', 'ee', 'eesti'],
      'iceland': ['iceland', 'is', 'island'],
      'malta': ['malta', 'mt'],
      'cyprus': ['cyprus', 'cy', 'kypros']
    };

    for (const [countryKey, patterns] of Object.entries(countryPatterns)) {
      for (const pattern of patterns) {
        if (urlLower.includes(pattern)) {
          return countryKey;
        }
      }
    }
    
    return null;
  };

  // Function to get country name from key
  const getCountryName = (countryKey) => {
    const countryNames = {
      'uk': 'United Kingdom',
      'gb': 'United Kingdom',
      'it': 'Italy',
      'italy': 'Italy',
      'ro': 'Romania',
      'romania': 'Romania',
      'fr': 'France',
      'france': 'France',
      'de': 'Germany',
      'germany': 'Germany',
      'es': 'Spain',
      'spain': 'Spain',
      'nl': 'Netherlands',
      'netherlands': 'Netherlands',
      'be': 'Belgium',
      'belgium': 'Belgium',
      'ch': 'Switzerland',
      'switzerland': 'Switzerland',
      'at': 'Austria',
      'austria': 'Austria',
      'pl': 'Poland',
      'poland': 'Poland',
      'cz': 'Czech Republic',
      'czech': 'Czech Republic',
      'hu': 'Hungary',
      'hungary': 'Hungary',
      'bg': 'Bulgaria',
      'bulgaria': 'Bulgaria',
      'gr': 'Greece',
      'greece': 'Greece',
      'se': 'Sweden',
      'sweden': 'Sweden',
      'no': 'Norway',
      'norway': 'Norway',
      'dk': 'Denmark',
      'denmark': 'Denmark',
      'fi': 'Finland',
      'finland': 'Finland',
      'pt': 'Portugal',
      'portugal': 'Portugal',
      'ie': 'Ireland',
      'ireland': 'Ireland',
      'lu': 'Luxembourg',
      'luxembourg': 'Luxembourg',
      'sk': 'Slovakia',
      'slovakia': 'Slovakia',
      'si': 'Slovenia',
      'slovenia': 'Slovenia',
      'hr': 'Croatia',
      'croatia': 'Croatia',
      'rs': 'Serbia',
      'serbia': 'Serbia',
      'ba': 'Bosnia and Herzegovina',
      'bosnia': 'Bosnia and Herzegovina',
      'me': 'Montenegro',
      'montenegro': 'Montenegro',
      'al': 'Albania',
      'albania': 'Albania',
      'mk': 'North Macedonia',
      'north macedonia': 'North Macedonia',
      'xk': 'Kosovo',
      'kosovo': 'Kosovo',
      'md': 'Moldova',
      'moldova': 'Moldova',
      'ua': 'Ukraine',
      'ukraine': 'Ukraine',
      'by': 'Belarus',
      'belarus': 'Belarus',
      'lt': 'Lithuania',
      'lithuania': 'Lithuania',
      'lv': 'Latvia',
      'latvia': 'Latvia',
      'ee': 'Estonia',
      'estonia': 'Estonia',
      'is': 'Iceland',
      'iceland': 'Iceland',
      'mt': 'Malta',
      'malta': 'Malta',
      'cy': 'Cyprus',
      'cyprus': 'Cyprus'
    };
    
    return countryNames[countryKey] || countryKey;
  };

  // Function to get color based on value
  const getColorForValue = (value) => {
    if (value === null || value === undefined || colorScale.max === colorScale.min) {
      return theme.noDataFill;
    }

    // Normalize value between 0 and 1
    const normalized = (value - colorScale.min) / (colorScale.max - colorScale.min);

    if (theme.gradientStart && theme.gradientEnd) {
      return interpolateColor(theme.gradientStart, theme.gradientEnd, normalized);
    }

    // Default darker red to green gradient
    const red = Math.round(180 * (1 - normalized));
    const green = Math.round(180 * normalized);
    const blue = 0;

    return `rgb(${red}, ${green}, ${blue})`;
  };

  // Function to get ISO country code from key
  const getCountryISO = (countryKey) => {
    const countryISOs = {
      'uk': 'GBR',
      'gb': 'GBR',
      'it': 'ITA',
      'italy': 'ITA',
      'ro': 'ROU',
      'romania': 'ROU',
      'fr': 'FRA',
      'france': 'FRA',
      'de': 'DEU',
      'germany': 'DEU',
      'es': 'ESP',
      'spain': 'ESP',
      'nl': 'NLD',
      'netherlands': 'NLD',
      'be': 'BEL',
      'belgium': 'BEL',
      'ch': 'CHE',
      'switzerland': 'CHE',
      'at': 'AUT',
      'austria': 'AUT',
      'pl': 'POL',
      'poland': 'POL',
      'cz': 'CZE',
      'czech': 'CZE',
      'hu': 'HUN',
      'hungary': 'HUN',
      'bg': 'BGR',
      'bulgaria': 'BGR',
      'gr': 'GRC',
      'greece': 'GRC',
      'se': 'SWE',
      'sweden': 'SWE',
      'no': 'NOR',
      'norway': 'NOR',
      'dk': 'DNK',
      'denmark': 'DNK',
      'fi': 'FIN',
      'finland': 'FIN',
      'pt': 'PRT',
      'portugal': 'PRT',
      'ie': 'IRL',
      'ireland': 'IRL',
      'lu': 'LUX',
      'luxembourg': 'LUX',
      'sk': 'SVK',
      'slovakia': 'SVK',
      'si': 'SVN',
      'slovenia': 'SVN',
      'hr': 'HRV',
      'croatia': 'HRV',
      'rs': 'SRB',
      'serbia': 'SRB',
      'ba': 'BIH',
      'bosnia': 'BIH',
      'me': 'MNE',
      'montenegro': 'MNE',
      'al': 'ALB',
      'albania': 'ALB',
      'mk': 'MKD',
      'north macedonia': 'MKD',
      'xk': 'XKX',
      'kosovo': 'XKX',
      'md': 'MDA',
      'moldova': 'MDA',
      'ua': 'UKR',
      'ukraine': 'UKR',
      'by': 'BLR',
      'belarus': 'BLR',
      'lt': 'LTU',
      'lithuania': 'LTU',
      'lv': 'LVA',
      'latvia': 'LVA',
      'ee': 'EST',
      'estonia': 'EST',
      'is': 'ISL',
      'iceland': 'ISL',
      'mt': 'MLT',
      'malta': 'MLT',
      'cy': 'CYP',
      'cyprus': 'CYP'
    };
    
    return countryISOs[countryKey] || countryKey.toUpperCase();
  };

  // Function to group banners by countries and then by retailers within each country
  const groupBannersByCountryAndRetailer = (banners) => {
    const countryGroups = {};
    
    banners.forEach(banner => {
      const country = banner.country || 'Unknown';
      const retailer = banner.retailer || 'Unknown';
      
      if (!countryGroups[country]) {
        countryGroups[country] = {
          banners: [],
          retailers: {}
        };
      }
      
      countryGroups[country].banners.push(banner);
      
      if (!countryGroups[country].retailers[retailer]) {
        countryGroups[country].retailers[retailer] = [];
      }
      countryGroups[country].retailers[retailer].push(banner);
    });
    
    // Sort countries by total banner count (descending), then alphabetically
    const sortedCountries = Object.entries(countryGroups)
      .sort(([a, dataA], [b, dataB]) => {
        if (dataB.banners.length !== dataA.banners.length) {
          return dataB.banners.length - dataA.banners.length;
        }
        return a.localeCompare(b);
      });
    
    // Sort retailers within each country by count (descending), then alphabetically
    sortedCountries.forEach(([country, data]) => {
      data.retailers = Object.entries(data.retailers)
        .sort(([a, bannersA], [b, bannersB]) => {
          if (bannersB.length !== bannersA.length) {
            return bannersB.length - bannersA.length;
          }
          return a.localeCompare(b);
        })
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
    });
    
    return Object.fromEntries(sortedCountries);
  };

  // Function to toggle group expansion (disabled - always expanded)
  const toggleGroup = (groupKey) => {
    // Groups are always expanded - no action needed
    return;
  };

  // Function to handle country click and scroll to banners
  const handleCountryClick = (countryData) => {
    if (countryData && countryData.bannerCount > 0) {
      setSelectedCountry(countryData.label);
      
      // Scroll to the banners section
      const bannersSection = document.getElementById('banners-section');
      if (bannersSection) {
        bannersSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Function to fetch banner data
  const fetchBannerData = async () => {
    try {
      setBannerLoading(true);
      setBannerError(null);
      const response = await axios.get(`${API_URL}/jti/banners-merged-data`);
      
      console.log('Banner API response:', response.data);
      
      // Check if the response has the expected structure
      if (!response.data || !response.data.columns || !response.data.data) {
        console.error('Unexpected banner API response structure:', response.data);
        setBannerError('Invalid banner data structure');
        return;
      }
      
      const { columns, data: rawData } = response.data;
      console.log('Banner columns:', columns);
      console.log('Banner raw data count:', rawData.length);
      
      // Convert raw data to objects with column names
      const processedBannerData = rawData.map(row => {
        const obj = {};
        columns.forEach((col, index) => {
          obj[col] = row[index];
        });
        return obj;
      });

      setBannerData(processedBannerData);
      
      // Create banner-based country mapping for map visualization
      const bannerCountryMapping = {};
      console.log('Processing banner data for countries:', processedBannerData.length, 'banners');
      
      processedBannerData.forEach((banner, index) => {
        // Debug: Log the first few banners to see their structure
        if (index < 3) {
          console.log(`Banner ${index}:`, banner);
          console.log('Available fields:', Object.keys(banner));
        }
        
        // Try multiple possible country field names
        let country = banner.country || banner.Country || banner.country_name || banner.countryName || banner.region || banner.Region;
        
        // If no explicit country, try to extract from URL fields
        if (!country) {
          const urlFields = Object.keys(banner).filter(key => 
            key.toLowerCase().includes('url') || 
            key.toLowerCase().includes('link') ||
            key.toLowerCase().includes('website')
          );
          
          for (const urlField of urlFields) {
            const extractedCountry = extractCountryFromUrl(banner[urlField]);
            if (extractedCountry) {
              country = extractedCountry;
              break;
            }
          }
        }
        
        if (country) {
          const countryKey = country.toLowerCase();
          
          if (!bannerCountryMapping[countryKey]) {
            bannerCountryMapping[countryKey] = {
              name: getCountryName(countryKey),
              bannerCount: 0,
              retailers: new Set(),
              categories: new Set(),
              hasBannerData: true
            };
          }
          bannerCountryMapping[countryKey].bannerCount++;
          
          // Try multiple possible retailer field names
          const retailer = banner.retailer || banner.Retailer || banner.retailer_name || banner.retailerName;
          if (retailer) {
            bannerCountryMapping[countryKey].retailers.add(retailer);
          }
          
          // Try multiple possible category field names
          const category = banner.category || banner.Category || banner.category_name || banner.categoryName;
          if (category) {
            bannerCountryMapping[countryKey].categories.add(category);
          }
        } else {
          if (index < 10) { // Only log first 10 to avoid spam
            console.log(`No country found for banner ${index}:`, banner);
          }
        }
      });
      
      console.log('Banner country mapping:', bannerCountryMapping);
      
      // Create banner-only map data
      const bannerGeoData = Object.entries(bannerCountryMapping).map(([countryKey, country]) => {
        const isoCode = getCountryISO(countryKey);
        return {
          id: isoCode,
          value: country.bannerCount,
          label: country.name,
          retailers: Array.from(country.retailers),
          categories: Array.from(country.categories),
          dataType: 'banner'
        };
      });
      
      console.log('Banner geo data created:', bannerGeoData);
      
      // Set map data to banner data only for better visualization
      if (bannerGeoData.length > 0) {
        setMapData(bannerGeoData);
        
        // Calculate color scale based on banner counts
        const bannerValues = bannerGeoData.map(d => d.value);
        setColorScale({
          min: Math.min(...bannerValues),
          max: Math.max(...bannerValues)
        });
        console.log('Map data updated with banner data');
      } else {
        console.log('No banner data found, keeping original map data');
        // Don't override the existing map data if no banner data is available
        // The original market data will remain visible
      }
      
    } catch (err) {
      console.error('Error fetching banner data:', err);
      setBannerError('Failed to load banner data');
    } finally {
      setBannerLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both product data and banner data
        const [productResponse, bannerResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/banners-merged-data`)
        ]);
        
        // Process product data
        const { columns: productColumns, data: productRawData } = productResponse.data;
        const processedProductData = productRawData.map(row => {
          const obj = {};
          productColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });

        setData(processedProductData);
        
        // Process banner data
        const { columns: bannerColumns, data: bannerRawData } = bannerResponse.data;
        const processedBannerData = bannerRawData.map(row => {
          const obj = {};
          bannerColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });

        setBannerData(processedBannerData);
        
        // Filter product data for UK and Italy
        const ukFiltered = processedProductData.filter(item => 
          item.Country && item.Country.toLowerCase().includes('uk') ||
          item.Region && item.Region.toLowerCase().includes('uk')
        );
        
        const italyFiltered = processedProductData.filter(item => 
          item.Country && item.Country.toLowerCase().includes('italy') ||
          item.Region && item.Region.toLowerCase().includes('italy')
        );

        setUkData(ukFiltered);
        setItalyData(italyFiltered);
        
        // Create combined country data mapping for map visualization
        const countryMapping = {};
        console.log('Processing product data for countries:', processedProductData.length, 'records');
        console.log('Processing banner data for countries:', processedBannerData.length, 'banners');
        
        // Process product data
        processedProductData.forEach((item, index) => {
          // Use only the "country" column from product data
          const country = item.country;
          
          // Debug: Log country values for first few records
          if (index < 10) {
            console.log(`Product ${index}: country="${country}"`);
          }
          
          if (country) {
            const countryKey = country.toLowerCase();
            
            if (!countryMapping[countryKey]) {
              countryMapping[countryKey] = {
                name: getCountryName(countryKey),
                productCount: 0,
                bannerCount: 0,
                brands: new Set(),
                retailers: new Set(),
                hasData: true
              };
            }
            countryMapping[countryKey].productCount++;
            if (item.Brand) {
              countryMapping[countryKey].brands.add(item.Brand);
            }
          }
        });
        
        // Process banner data
        processedBannerData.forEach((banner, index) => {
          // Use only the "country" column from banner data
          const country = banner.country;
          
          // Debug: Log country values for first few records
          if (index < 10) {
            console.log(`Banner ${index}: country="${country}"`);
          }
          
          if (country) {
            const countryKey = country.toLowerCase();
            
            if (!countryMapping[countryKey]) {
              countryMapping[countryKey] = {
                name: getCountryName(countryKey),
                productCount: 0,
                bannerCount: 0,
                brands: new Set(),
                retailers: new Set(),
                hasData: true
              };
            }
            countryMapping[countryKey].bannerCount++;
            
            // Try multiple possible retailer field names
            const retailer = banner.retailer || banner.Retailer || banner.retailer_name || banner.retailerName;
            if (retailer) {
              countryMapping[countryKey].retailers.add(retailer);
            }
          }
        });
        
        console.log('Combined country mapping:', countryMapping);
        setCountryData(countryMapping);

        // Prepare data for React Simple Maps - use total count (products + banners)
        const geoData = Object.entries(countryMapping).map(([countryKey, country]) => {
          const isoCode = getCountryISO(countryKey);
          const totalCount = country.productCount + country.bannerCount;
          console.log(`Mapping ${countryKey} -> ISO: ${isoCode}, Products: ${country.productCount}, Banners: ${country.bannerCount}, Total: ${totalCount}`);
          return {
            id: isoCode,
            value: totalCount,
            label: country.name,
            productCount: country.productCount,
            bannerCount: country.bannerCount,
            brands: Array.from(country.brands),
            retailers: Array.from(country.retailers)
          };
        });
        setMapData(geoData);
        
        // Calculate color scale based on total data values
        if (geoData.length > 0) {
          const values = geoData.map(d => d.value);
          setColorScale({
            min: Math.min(...values),
            max: Math.max(...values)
          });
        }
        
        console.log('Final geo data for map:', geoData);
        console.log('Color scale:', { min: Math.min(...geoData.map(d => d.value)), max: Math.max(...geoData.map(d => d.value)) });
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load market data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const extractPriceData = (data, brand) => {
    if (!data || data.length === 0) return null;
    
    const brandData = data.filter(item => 
      item.Brand && item.Brand.toLowerCase().includes(brand.toLowerCase())
    );
    
    if (brandData.length === 0) return null;
    
    // Extract price information
    const priceFields = Object.keys(brandData[0]).filter(key => 
      key.toLowerCase().includes('price') || 
      key.toLowerCase().includes('cost') ||
      key.toLowerCase().includes('value')
    );
    
    return {
      brand,
      priceData: priceFields.map(field => ({
        field,
        value: brandData[0][field]
      })).filter(item => item.value !== null && item.value !== undefined)
    };
  };

  const extractCommunicationData = (data, brand) => {
    if (!data || data.length === 0) return null;
    
    const brandData = data.filter(item => 
      item.Brand && item.Brand.toLowerCase().includes(brand.toLowerCase())
    );
    
    if (brandData.length === 0) return null;
    
    // Extract communication/message fields
    const messageFields = Object.keys(brandData[0]).filter(key => 
      key.toLowerCase().includes('message') || 
      key.toLowerCase().includes('communication') ||
      key.toLowerCase().includes('promo') ||
      key.toLowerCase().includes('campaign') ||
      key.toLowerCase().includes('description')
    );
    
    return {
      brand,
      messages: messageFields.map(field => ({
        field,
        value: brandData[0][field]
      })).filter(item => item.value !== null && item.value !== undefined)
    };
  };

  const renderMarketCard = (country, data, brand) => {
    const priceInfo = extractPriceData(data, brand);
    const communicationInfo = extractCommunicationData(data, brand);
    
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Globe size={24} style={{ marginRight: 8, color: '#1976d2' }} />
            <Typography variant="h6" component="h2">
              {country} - {brand}
            </Typography>
          </Box>
          
          {priceInfo && (
            <Box mb={2}>
              <Box display="flex" alignItems="center" mb={1}>
                <CurrencyDollar size={20} style={{ marginRight: 8, color: '#2e7d32' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Price Information
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priceInfo.priceData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.field}</TableCell>
                        <TableCell>{item.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {communicationInfo && (
            <Box>
              <Box display="flex" alignItems="center" mb={1}>
                <ChatCircle size={20} style={{ marginRight: 8, color: '#0288d1' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Communication & Messages
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {communicationInfo.messages.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.field}</TableCell>
                        <TableCell>{item.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {!priceInfo && !communicationInfo && (
            <Typography variant="body2" color="text.secondary">
              No specific data found for {brand} in {country}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const sampleOptions = useMemo(() => {
    const options = {
      countries: [],
      brands: [],
      retailers: [],
      categories: [],
      flavors: [],
      strengths: []
    };
    if (!data || data.length === 0) return options;

    const countries = new Set();
    const brands = new Set();
    const retailers = new Set();
    const categories = new Set();
    const flavors = new Set();
    const strengths = new Set();

    data.forEach((item) => {
      if (item.country) countries.add(item.country);
      if (item.brand) brands.add(item.brand);
      if (item.retailer) retailers.add(item.retailer);
      if (item.clean_category) categories.add(item.clean_category);
      if (item.flavor_intensity) flavors.add(item.flavor_intensity);
      if (item.intensity) strengths.add(item.intensity);
    });

    options.countries = Array.from(countries).sort();
    options.brands = Array.from(brands).sort();
    options.retailers = Array.from(retailers).sort();
    options.categories = Array.from(categories).sort();
    options.flavors = Array.from(flavors).sort();
    options.strengths = Array.from(strengths).sort();

    return options;
  }, [data]);

  const filteredSampleData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter((item) => {
      if (sampleFilters.country && item.country !== sampleFilters.country) return false;
      if (sampleFilters.retailer && item.retailer !== sampleFilters.retailer) return false;
      if (sampleFilters.brand && item.brand !== sampleFilters.brand) return false;
      if (sampleFilters.category && item.clean_category !== sampleFilters.category) return false;
      if (sampleFilters.flavor && item.flavor_intensity !== sampleFilters.flavor) return false;
      if (sampleFilters.strength && item.intensity !== sampleFilters.strength) return false;
      return true;
    });
  }, [data, sampleFilters]);

  const buildTopList = (items, key, limit = 6) => {
    const counts = new Map();
    items.forEach((item) => {
      const label = item[key] || 'Unknown';
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };

  const sampleStats = useMemo(() => {
    const source = filteredSampleData;
    return {
      total: source.length,
      topBrands: buildTopList(source, 'brand'),
      topRetailers: buildTopList(source, 'retailer'),
      categories: buildTopList(source, 'clean_category')
    };
  }, [filteredSampleData]);

  const renderBarList = (title, items, accentColor, emptyLabel) => {
    const maxValue = Math.max(1, ...items.map((item) => item.value));
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            {items.map((item) => (
              <Box key={item.label}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 6,
                    backgroundColor: '#e0e0e0'
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(item.value / maxValue) * 100}%`,
                      borderRadius: 6,
                      backgroundColor: accentColor
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const resolveCategoryValue = (targetLabel) => {
    const normalized = targetLabel.toLowerCase();
    const match = sampleOptions.categories.find(
      (category) => category && category.toLowerCase() === normalized
    );
    return match || '';
  };

  const handleSampleTabChange = (_, newValue) => {
    if (!newValue) return;
    setSampleTab(newValue);
    let categoryValue = '';
    if (newValue === 'sticks') {
      categoryValue = resolveCategoryValue('sticks') || resolveCategoryValue('consumables');
    } else if (newValue === 'devices') {
      categoryValue = resolveCategoryValue('devices');
    } else if (newValue === 'starter_kits') {
      categoryValue = resolveCategoryValue('starter kits') || resolveCategoryValue('starter kits');
    }
    setSampleFilters((prev) => ({ ...prev, category: categoryValue }));
  };

    // Function to render banner preview card
  const renderBannerCard = (banner, index) => {
    const hasImage = banner.image || banner.image_mobile;
    const imageUrl = banner.image || banner.image_mobile;
    
    return (
      <ImageListItem key={index} sx={{ cursor: 'pointer' }} onClick={() => {
        setSelectedBanner(banner);
        setBannerDialogOpen(true);
      }}>
        <Box
          sx={{
            width: '100%',
            height: 400,
            bgcolor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          {/* Image section */}
          <Box sx={{ height: 280, position: 'relative' }}>
            {hasImage ? (
              <>
                <img
                  src={imageUrl}
                  alt={banner.text || banner.description || 'Banner'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <Box 
                  display="none" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <ImageIcon size={48} color="#ccc" />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Image Failed to Load
                  </Typography>
                </Box>
              </>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center"
                sx={{ height: '100%' }}
              >
                <ImageIcon size={48} color="#ccc" />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  No Image
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Metadata section below image */}
          <Box sx={{ p: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" noWrap sx={{ mb: 1, fontWeight: 'bold' }}>
                {banner.text || banner.description || 'Banner'}
              </Typography>
              
              <Box sx={{ mb: 1 }}>
                <Chip 
                  size="small" 
                  label={banner.category || 'Unknown'} 
                  color="primary" 
                  variant="outlined"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
                {banner.country && (
                  <Chip 
                    size="small" 
                    label={banner.country} 
                    color="secondary" 
                    variant="outlined"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                )}
                {banner.retailer && (
                  <Chip 
                    size="small" 
                    label={banner.retailer} 
                    color="success" 
                    variant="outlined"
                    sx={{ mb: 0.5 }}
                  />
                )}
              </Box>
              
              {banner.cta_text && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <strong>CTA:</strong> {banner.cta_text}
                </Typography>
              )}
              
              {banner.url && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <strong>URL:</strong> {banner.url.length > 50 ? banner.url.substring(0, 50) + '...' : banner.url}
                </Typography>
              )}
            </Box>
            
            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                variant="contained"
                startIcon={<LinkIcon size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (banner.url) window.open(banner.url, '_blank');
                }}
                disabled={!banner.url}
              >
                Open Link
              </Button>
            </Box>
          </Box>
        </Box>
      </ImageListItem>
    );
  };

  // Function to render grouped banners by country and retailer
  const renderGroupedBanners = () => {
    if (!bannerData || bannerData.length === 0) return null;
    
    const groupedBanners = groupBannersByCountryAndRetailer(bannerData);
    let countryEntries = Object.entries(groupedBanners);
    
    // Filter by selected country if one is selected
    if (selectedCountry) {
      countryEntries = countryEntries.filter(([country]) => {
        // Try to match by country name or country code
        const countryLower = country.toLowerCase();
        const selectedLower = selectedCountry.toLowerCase();
        
        // Direct match
        if (countryLower === selectedLower) return true;
        
        // Try to match country code to country name
        const countryCodeMap = {
          'ro': 'romania',
          'it': 'italy',
          'uk': 'united kingdom',
          'gb': 'united kingdom',
          'fr': 'france',
          'de': 'germany',
          'es': 'spain',
          'nl': 'netherlands',
          'be': 'belgium',
          'ch': 'switzerland',
          'at': 'austria',
          'pl': 'poland',
          'cz': 'czech republic',
          'hu': 'hungary',
          'bg': 'bulgaria',
          'gr': 'greece',
          'se': 'sweden',
          'no': 'norway',
          'dk': 'denmark',
          'fi': 'finland',
          'pt': 'portugal',
          'ie': 'ireland',
          'lu': 'luxembourg',
          'sk': 'slovakia',
          'si': 'slovenia',
          'hr': 'croatia',
          'rs': 'serbia',
          'ba': 'bosnia and herzegovina',
          'me': 'montenegro',
          'al': 'albania',
          'mk': 'north macedonia',
          'xk': 'kosovo',
          'md': 'moldova',
          'ua': 'ukraine',
          'by': 'belarus',
          'lt': 'lithuania',
          'lv': 'latvia',
          'ee': 'estonia',
          'is': 'iceland',
          'mt': 'malta',
          'cy': 'cyprus'
        };
        
        return countryCodeMap[countryLower] === selectedLower || 
               countryCodeMap[selectedLower] === countryLower;
      });
    }
    
    return (
      <Box>
        {countryEntries.map(([country, countryData]) => {
          const isCountryExpanded = expandedGroups.has(country);
          const retailerEntries = Object.entries(countryData.retailers);
          
          return (
            <Card key={country} sx={{ mb: 2 }}>
              <CardContent>
                                 <Box 
                   display="flex" 
                   alignItems="center" 
                   justifyContent="space-between"
                 >
                  <Box display="flex" alignItems="center">
                    <MapPin size={20} style={{ marginRight: 8, color: '#1976d2' }} />
                    <Typography variant="h6">
                      {country} ({countryData.banners.length} banners)
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      size="small" 
                      label={`${retailerEntries.length} retailers`}
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
                
                                 {(
                  <Box mt={2}>
                    {retailerEntries.map(([retailer, retailerBanners]) => {
                      const retailerKey = `${country}-${retailer}`;
                      const isRetailerExpanded = expandedGroups.has(retailerKey);
                      
                      return (
                        <Card key={retailerKey} sx={{ mb: 2, ml: 2 }} variant="outlined">
                          <CardContent>
                                                         <Box 
                               display="flex" 
                               alignItems="center" 
                               justifyContent="space-between"
                             >
                              <Box display="flex" alignItems="center">
                                <Storefront size={16} style={{ marginRight: 8, color: '#2e7d32' }} />
                                <Typography variant="subtitle1">
                                  {retailer} ({retailerBanners.length} banners)
                                </Typography>
                              </Box>
                            </Box>
                            
                                                                                      {(
                               <Box mt={2}>
                                 <ImageList 
                                   cols={4} 
                                   gap={16}
                                   sx={{
                                     width: '100%',
                                     '& .MuiImageListItem-root': {
                                       minWidth: '200px !important'
                                     }
                                   }}
                                 >
                                   {retailerBanners.map((banner, index) => renderBannerCard(banner, index))}
                                 </ImageList>
                               </Box>
                             )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  // Banner detail dialog
  const BannerDetailDialog = () => (
    <Dialog
      open={bannerDialogOpen}
      onClose={() => setBannerDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Storefront size={24} style={{ marginRight: 8 }} />
          Banner Details
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedBanner && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  bgcolor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              >
                {(selectedBanner.image || selectedBanner.image_mobile) ? (
                  <img
                    src={selectedBanner.image || selectedBanner.image_mobile}
                    alt={selectedBanner.text || selectedBanner.description || 'Banner'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <ImageIcon size={64} color="#ccc" />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No Image Available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Banner Information
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {selectedBanner.category && (
                        <TableRow>
                          <TableCell><strong>Category:</strong></TableCell>
                          <TableCell>{selectedBanner.category}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.source_page && (
                        <TableRow>
                          <TableCell><strong>Source Page:</strong></TableCell>
                          <TableCell>{selectedBanner.source_page}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.country && (
                        <TableRow>
                          <TableCell><strong>Country:</strong></TableCell>
                          <TableCell>{selectedBanner.country}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.retailer && (
                        <TableRow>
                          <TableCell><strong>Retailer:</strong></TableCell>
                          <TableCell>{selectedBanner.retailer}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.text && (
                        <TableRow>
                          <TableCell><strong>Text:</strong></TableCell>
                          <TableCell>{selectedBanner.text}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.description && (
                        <TableRow>
                          <TableCell><strong>Description:</strong></TableCell>
                          <TableCell>{selectedBanner.description}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.cta_text && (
                        <TableRow>
                          <TableCell><strong>CTA Text:</strong></TableCell>
                          <TableCell>{selectedBanner.cta_text}</TableCell>
                        </TableRow>
                      )}
                      {selectedBanner.url && (
                        <TableRow>
                          <TableCell><strong>URL:</strong></TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LinkIcon />}
                              onClick={() => window.open(selectedBanner.url, '_blank')}
                            >
                              Open Link
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBannerDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* <Alert severity="info" sx={{ mb: 2 }}>
        All data for Ploom, Glo and Iqos in RO, IT, UK will be available on 8th August (data transformations are being handled)
      </Alert> */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: theme.titleColor }}>
        {theme.title}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {theme.subtitle}
      </Typography>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Interactive World Map Visualization */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom sx={{ color: theme.sectionTitleColor }}>
          {theme.mapTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          {theme.mapHint}
        </Typography>
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box 
                  sx={{ 
                    height: 500, 
                    background: theme.mapContainerBg,
                    borderRadius: 2,
                    border: theme.mapContainerBorder,
                    overflow: 'hidden'
                  }}
                >
                                                        {mapData && mapData.length > 0 ? (
                     <Box 
                       sx={{ position: 'relative' }}
                                               onMouseMove={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPosition({ 
                            x: e.clientX - rect.left, 
                            y: e.clientY - rect.top 
                          });
                        }}
                     >
                       {/* Zoom Controls */}
                       <Box
                         sx={{
                           position: 'absolute',
                           top: 10,
                           right: 10,
                           zIndex: 1000,
                           display: 'flex',
                           flexDirection: 'column',
                           gap: 1
                         }}
                       >
                         <Box
                           sx={{
                             width: 32,
                             height: 32,
                            bgcolor: theme.zoomControlBg,
                            border: `1px solid ${theme.zoomControlBorder}`,
                             borderRadius: 1,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             cursor: 'pointer',
                            '&:hover': { bgcolor: theme.zoomControlHoverBg }
                           }}
                                                       onClick={() => setPosition(prev => ({
                              ...prev,
                              zoom: Math.min(prev.zoom * 1.5, 6)
                            }))}
                         >
                           <Typography variant="h6">+</Typography>
                         </Box>
                         <Box
                           sx={{
                             width: 32,
                             height: 32,
                            bgcolor: theme.zoomControlBg,
                            border: `1px solid ${theme.zoomControlBorder}`,
                             borderRadius: 1,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             cursor: 'pointer',
                            '&:hover': { bgcolor: theme.zoomControlHoverBg }
                           }}
                                                       onClick={() => setPosition(prev => ({
                              ...prev,
                              zoom: Math.max(prev.zoom / 1.5, 1)
                            }))}
                         >
                           <Typography variant="h6">−</Typography>
                         </Box>
                         <Box
                           sx={{
                             width: 32,
                             height: 32,
                            bgcolor: theme.zoomControlBg,
                            border: `1px solid ${theme.zoomControlBorder}`,
                             borderRadius: 1,
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             cursor: 'pointer',
                            '&:hover': { bgcolor: theme.zoomControlHoverBg }
                           }}
                                                       onClick={() => setPosition({ coordinates: [15, 55], zoom: 3 })}
                         >
                           <Typography variant="caption">🏠</Typography>
                         </Box>
                       </Box>
                                               <ComposableMap
                          projection="geoMercator"
                                                     projectionConfig={{
                             scale: 200,
                             center: [15, 55]
                           }}
                        >
                          <ZoomableGroup
                            zoom={position.zoom}
                            center={position.coordinates}
                            onMoveEnd={setPosition}
                                                         minZoom={1}
                             maxZoom={6}
                          >
                                                       <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json">
                              {({ geographies }) => {
                                console.log('Total geographies loaded:', geographies.length);
                                console.log('Available ISO codes:', geographies.map(g => g.properties.ISO_A3).slice(0, 10));
                                
                                                                 return geographies.map((geo) => {
                                   // Debug: Log all available properties for the first few countries
                                   if (geo.rsmKey < 5) {
                                     console.log(`Country ${geo.rsmKey} properties:`, geo.properties);
                                   }
                                   
                                   // Try multiple ways to match the country
                                   let countryData = mapData.find(d => d.id === geo.properties.ISO_A3);
                                   
                                   // If not found, try matching by country name (check different possible property names)
                                   if (!countryData) {
                                     const countryName = geo.properties.NAME || geo.properties.name || geo.properties.ADMIN || geo.properties.admin;
                                     if (countryName) {
                                       countryData = mapData.find(d => 
                                         d.label.toLowerCase() === countryName.toLowerCase()
                                       );
                                     }
                                   }
                                   
                                   // Debug for specific countries
                                   if (geo.properties.ISO_A3 === 'ITA' || geo.properties.ISO_A3 === 'ROU' || geo.properties.ISO_A3 === 'DEU') {
                                     const countryName = geo.properties.NAME || geo.properties.name || geo.properties.ADMIN || geo.properties.admin;
                                     console.log(`Debug ${countryName}: ISO=${geo.properties.ISO_A3}, found=${!!countryData}`);
                                     console.log('Available mapData IDs:', mapData.map(d => d.id));
                                     console.log('Available mapData labels:', mapData.map(d => d.label));
                                   }
                                   
                                   const hasData = countryData !== undefined;
                                   const fillColor = hasData ? getColorForValue(countryData.value) : theme.noDataFill;
                                   
                                   return (
                                                                          <Geography
                                       key={geo.rsmKey}
                                       geography={geo}
                                       onMouseEnter={() => {
                                         if (hasData) {
                                           const recordInfo = countryData.productCount > 0 ? `${countryData.productCount} records` : '';
                                           const bannerInfo = countryData.bannerCount > 0 ? `${countryData.bannerCount} banners` : '';
                                           const totalInfo = [recordInfo, bannerInfo].filter(Boolean).join(', ');
                                           
                                           const brandInfo = countryData.brands && countryData.brands.length > 0 
                                             ? `\nBrands: ${countryData.brands.join(', ')}` 
                                             : '';
                                           
                                           setTooltipContent(
                                             `${countryData.label}: ${totalInfo}${brandInfo}`
                                           );
                                         } else {
                                           const countryName = geo.properties.NAME || geo.properties.name || geo.properties.ADMIN || geo.properties.admin || 'Unknown';
                                           setTooltipContent(`${countryName}: No data available`);
                                         }
                                       }}
                                       onMouseLeave={() => {
                                         setTooltipContent("");
                                       }}
                                       onClick={() => {
                                         if (hasData) {
                                           handleCountryClick(countryData);
                                         }
                                       }}
                                                                              style={{
                                          default: {
                                            fill: fillColor,
                                            stroke: theme.mapStroke,
                                            strokeWidth: 0.5,
                                            outline: "none",
                                            cursor: hasData && countryData.bannerCount > 0 ? "pointer" : "default",
                                          },
                                          hover: {
                                            fill: hasData ? getColorForValue(countryData.value) : theme.noDataHoverFill,
                                            stroke: theme.mapStroke,
                                            strokeWidth: 0.5,
                                            outline: "none",
                                            cursor: hasData && countryData.bannerCount > 0 ? "pointer" : "default",
                                          },
                                          pressed: {
                                            fill: hasData ? getColorForValue(countryData.value) : theme.noDataHoverFill,
                                            stroke: theme.mapStroke,
                                            strokeWidth: 0.5,
                                            outline: "none",
                                            cursor: hasData && countryData.bannerCount > 0 ? "pointer" : "default",
                                          },
                                        }}
                                    />
                                                                     );
                                 });
                               }}
                            </Geographies>
                         </ZoomableGroup>
                       </ComposableMap>
                       
                                               {/* Tooltip */}
                        {tooltipContent && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: tooltipPosition.y - 60,
                              left: tooltipPosition.x + 15,
                              background: theme.tooltipBg,
                              padding: 1.5,
                              border: `1px solid ${theme.tooltipBorder}`,
                              borderRadius: 2,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              pointerEvents: 'none',
                              maxWidth: 200,
                              whiteSpace: 'pre-line'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                              {tooltipContent}
                            </Typography>
                          </Box>
                        )}
                     </Box>
                   ) : (
                     <Box 
                       display="flex" 
                       justifyContent="center" 
                       alignItems="center" 
                       height="100%"
                     >
                       <Typography variant="body1" color="text.secondary">
                         No geographic data available
                       </Typography>
                     </Box>
                   )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ color: theme.sectionTitleColor }}>
                    {theme.dataDistributionTitle}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Countries with data:
                  </Typography>
                  <Box mb={2}>
                    {mapData.map((country) => {
                      const productText = country.productCount > 0 ? `${country.productCount} products` : '';
                      const bannerText = country.bannerCount > 0 ? `${country.bannerCount} banners` : '';
                      const totalText = [productText, bannerText].filter(Boolean).join(', ');
                      
                      return (
                        <Chip 
                          key={country.id}
                          icon={<MapPin size={16} />} 
                          label={`${country.label} (${totalText})`}
                          color="success" 
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1, mr: 1 }}
                        />
                      );
                    })}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Retailers Found:
                  </Typography>
                  <Box mb={2}>
                    {(() => {
                      const allRetailers = new Set();
                      mapData.forEach(country => {
                        if (country.retailers) {
                          country.retailers.forEach(retailer => allRetailers.add(retailer));
                        }
                      });
                      return Array.from(allRetailers).map(retailer => (
                        <Chip 
                          key={retailer}
                          label={retailer} 
                          color="success" 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }} 
                        />
                      ));
                    })()}
                  </Box>
                  

                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {theme.legendTitle}
                  </Typography>
                  <Box>
                    {theme.legendItems.map((item) => (
                      <Box display="flex" alignItems="center" mb={1} key={item.label}>
                        <Box
                          width={16}
                          height={16}
                          bgcolor={item.color}
                          border={item.border ? `1px solid ${item.border}` : 'none'}
                          borderRadius={1}
                          mr={1}
                        />
                        <Typography variant="caption">{item.label}</Typography>
                      </Box>
                    ))}
                    <Typography variant="caption" color="text.secondary">
                      Hover over countries for detailed information
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {showBanners && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 2,
              animation: 'bounce 1.5s infinite'
            }}
          >
            <Typography 
              variant="body2" 
              color="text.primary" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontWeight: 500,
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)'
                  },
                  '40%': {
                    transform: 'translateY(-5px)'
                  },
                  '60%': {
                    transform: 'translateY(-2px)'
                  }
                }
              }}
            >
              Scroll down to explore banners ↓
            </Typography>
          </Box>
        )}
      </Box>

      {variant === 'green' && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom sx={{ color: theme.sectionTitleColor }}>
            Sample Insights
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Example visualizations using data from `jti/global-merged-data` with filters arranged to match the requested layout.
          </Typography>

          <Box display="flex" gap={2} alignItems="stretch">
            {/* Center Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Main switches (product type, time, price type, etc)
                  </Typography>
                  <Tabs
                    value={sampleTab}
                    onChange={handleSampleTabChange}
                    variant="fullWidth"
                    sx={{
                      minHeight: 36,
                      '& .MuiTab-root': {
                        minHeight: 36,
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none'
                      }
                    }}
                  >
                    <Tab label="Sticks" value="sticks" />
                    <Tab label="Devices" value="devices" />
                    <Tab label="Starter kits" value="starter_kits" />
                  </Tabs>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    <Chip label={`Records: ${sampleStats.total}`} color="success" />
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Main chart / table
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {renderBarList(
                        'Top Brands (records)',
                        sampleStats.topBrands,
                        '#1b5e20',
                        'No brand data for the current filters.'
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {renderBarList(
                        'Top Retailers (records)',
                        sampleStats.topRetailers,
                        '#2e7d32',
                        'No retailer data for the current filters.'
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {renderBarList(
                        'Category Mix (records)',
                        sampleStats.categories,
                        '#66bb6a',
                        'No category data for the current filters.'
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>

            {/* Filters */}
            <Card sx={{ width: 240 }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Filters
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    label="Country"
                    value={sampleFilters.country}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, country: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Retailer</InputLabel>
                  <Select
                    label="Retailer"
                    value={sampleFilters.retailer}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, retailer: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.retailers.map((retailer) => (
                      <MenuItem key={retailer} value={retailer}>
                        {retailer}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    label="Brand"
                    value={sampleFilters.brand}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, brand: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.brands.map((brand) => (
                      <MenuItem key={brand} value={brand}>
                        {brand}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={sampleFilters.category}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, category: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Flavour</InputLabel>
                  <Select
                    label="Flavour"
                    value={sampleFilters.flavor}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, flavor: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.flavors.map((flavor) => (
                      <MenuItem key={flavor} value={flavor}>
                        {flavor}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Strength</InputLabel>
                  <Select
                    label="Strength"
                    value={sampleFilters.strength}
                    onChange={(e) => setSampleFilters((prev) => ({ ...prev, strength: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    {sampleOptions.strengths.map((strength) => (
                      <MenuItem key={strength} value={strength}>
                        {strength}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {showBanners && (
        <>
          {/* Banner Information Section */}
          <Box mb={4} id="banners-section">
            <Typography variant="h5" gutterBottom>
              🎯 Banner Information & Preview Cards
              {selectedCountry && (
                <Chip 
                  label={`Selected: ${selectedCountry}`}
                  color="primary"
                  size="small"
                  sx={{ ml: 2 }}
                  onDelete={() => setSelectedCountry(null)}
                />
              )}
            </Typography>
            <Card>
              <CardContent>
                {bannerLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </Box>
                ) : bannerError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {bannerError}
                  </Alert>
                ) : bannerData && bannerData.length > 0 ? (
                  <Box>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Storefront size={24} style={{ marginRight: 8, color: '#1976d2' }} />
                      <Typography variant="h6">
                        Global Banner Collection ({bannerData.length} banners)
                      </Typography>
                    </Box>
                    
                                     <Typography variant="body2" color="text.secondary" gutterBottom>
                       Banners are organized by Countries → Retailers. All groups are expanded by default. Click on any banner to view detailed information.
                     </Typography>
                    
                    {renderGroupedBanners()}
                  </Box>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <Typography variant="body1" color="text.secondary">
                      No banner data available
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Banner Detail Dialog */}
          <BannerDetailDialog />
        </>
      )}
    </Box>
  );
};

export default WorldMapView; 